import { useState, useCallback, useEffect } from "react";
import moment from 'moment';
import ScheduleService from '@/services/ScheduleService';

export default function useScheduleData(start = null, end = null) {
  // Data State
  const [data, setData] = useState([]);
  
  // UI State
  const [loading, setLoading] = useState(true);
  
  const [removing, setRemoving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [error, setError] = useState(null);
  
  // Pagination State
  const [totalCount, setTotalCount] = useState(0);
  
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    let result;
    if (start && end) {
      // Format dates using moment
      const formattedStart = moment(start).format('YYYY-MM-DD');
      const formattedEnd = moment(end).format('YYYY-MM-DD');
      result = await ScheduleService.getByDateRange(formattedStart, formattedEnd);
    } else {
      result = await ScheduleService.getAll();
    }
    
    if (result.error) {
      setError(result.error);
      setData([]);
      setTotalCount(0);
    } else {
      setData(result.data);
      setTotalCount(result.data.length);
    }
    
    setLoading(false);
  }, [start, end]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const create = async (newRecord) => {
    setCreating(true);
    setError(null);
    const result = await ScheduleService.create(newRecord);
    if (result.error) {
      setError(result.error);
      setCreating(false);
      return false;
    } else {
      await loadData();
      setCreating(false);
      return true;
    }
  };

  const remove = async (id) => {
    setRemoving(true);
    setError(null);
    const result = await ScheduleService.deleteOne(id);
    if (result.error) {
      setError(result.error);
      setRemoving(false);
      return false;
    } else {
      await loadData();
      setRemoving(false);
      return true;
    }
  };

  const update = async (id, updatedRecord) => {
    setUpdating(true);
    setError(null);
    const result = await ScheduleService.update(id, updatedRecord);
    if (result.error) {
      setError(result.error);
      setUpdating(false);
      return false;
    } else {
      await loadData();
      setUpdating(false);
      return true;
    }
  };

  return {
    data,
    totalCount,
    
    loading,
    creating,
    updating,
    removing,
    setLoading,
    setCreating,
    setUpdating,
    setRemoving,
    error,
    clearError,

    loadData,
    create,
    remove,
    update
  };
}
